import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Search, MapPin, Clock, Heart, ArrowRight,
    SlidersHorizontal, X, ChevronUp, ChevronDown, Star,
} from 'lucide-react';
import JobDetailModal from './JobDetailModal';

/* ─── Helpers ─── */
function formatDate(dt) {
    if (!dt) return '';
    try { return new Date(dt).toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' }); }
    catch { return dt; }
}
function formatTime(dt) {
    if (!dt) return '';
    try { return new Date(dt).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
}
function timeAgo(dt) {
    if (!dt) return '';
    const diff = Date.now() - new Date(dt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

const AVATAR_PALETTE = ['#d97706', '#0f6f57', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#0891b2'];
function avatarColor(str = '') {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function jobTags(job) {
    if (!job.tags) return [];
    return job.tags.split(',').map(t => t.trim()).filter(Boolean);
}
function jobReqs(job) {
    if (!job.requirements) return [];
    return job.requirements.split('||').map(r => r.trim()).filter(Boolean);
}
/* FIX: backend sends firstname/lastname (lowercase n), not firstName/lastName */
function posterName(job) {
    const u = job.createdBy;
    if (!u) return 'Unknown';
    const f = u.firstname || u.firstName || '';
    const l = u.lastname  || u.lastName  || '';
    if (f || l) return `${f} ${l}`.trim();
    return u.username || 'Unknown';
}

function getToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

/* ─── Tag input (filter drawer) ─── */
const POPULAR_FILTER_TAGS = [
    'dog walking', 'cleaning', 'moving furniture', 'bar shift',
    'babysitting', 'gardening', 'barista', 'flat-pack assembly',
    'tutoring', 'photography', 'event help', 'cat sitting',
];

function FilterTagInput({ tags, onChange }) {
    const [q, setQ] = useState('');
    const inputRef = useRef(null);

    const suggestions = q.trim()
        ? POPULAR_FILTER_TAGS.filter(t => t.includes(q.toLowerCase()) && !tags.includes(t)).slice(0, 5)
        : POPULAR_FILTER_TAGS.filter(t => !tags.includes(t)).slice(0, 6);

    const add = t => { onChange([...tags, t]); setQ(''); inputRef.current?.focus(); };
    const remove = t => onChange(tags.filter(x => x !== t));

    const onKeyDown = e => {
        if (e.key === 'Enter' && q.trim()) {
            e.preventDefault();
            const match = suggestions[0];
            if (match) add(match);
            else if (q.trim().length >= 2) { add(q.trim().toLowerCase()); setQ(''); }
        } else if (e.key === 'Backspace' && !q && tags.length) {
            remove(tags[tags.length - 1]);
        }
    };

    return (
        <div>
            {tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                    {tags.map(t => (
                        <span key={t} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '4px 8px 4px 10px', background: 'var(--teal-100)',
                            color: 'var(--teal-800)', borderRadius: 'var(--r-pill)', fontSize: '13px', fontWeight: 500,
                        }}>
                            {t}
                            <button onClick={() => remove(t)} style={{
                                width: '16px', height: '16px', display: 'grid', placeItems: 'center',
                                background: 'color-mix(in srgb, var(--teal-800) 16%, transparent)',
                                color: 'var(--teal-800)', borderRadius: '50%', border: 'none', cursor: 'pointer',
                            }}><X size={9} /></button>
                        </span>
                    ))}
                </div>
            )}
            <div className="gj-drawer-input">
                <Search size={14} color="var(--ink-400)" />
                <input ref={inputRef}
                    placeholder={tags.length === 0 ? 'e.g. dog walking, cleaning…' : 'Add another tag'}
                    value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKeyDown}
                />
            </div>
            <div className="gj-chip-grid" style={{ marginTop: '6px' }}>
                {suggestions.map(t => (
                    <button key={t} className="gj-chip-toggle" onClick={() => add(t)}>{t}</button>
                ))}
            </div>
        </div>
    );
}

/* ─── Filter drawer ─── */
const SORT_OPTIONS = [
    { v: '',             l: 'Newest first' },
    { v: 'payment-high', l: 'Highest pay' },
    { v: 'payment-low',  l: 'Lowest pay' },
    { v: 'date-oldest',  l: 'Oldest first' },
];
const DURATION_OPTIONS = ['< 1 hr', '1–3 hrs', 'Half day', 'Full day', 'Multi-day'];
const WHEN_OPTIONS = [
    { v: 'any',      l: 'Any time' },
    { v: 'today',    l: 'Today' },
    { v: 'tomorrow', l: 'Tomorrow' },
    { v: 'weekend',  l: 'This weekend' },
    { v: 'week',     l: 'This week' },
];

function FilterDrawer({ open, onClose, filters, setFilters, onApply, onReset }) {
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);

    useEffect(() => {
        axios.get('https://countriesnow.space/api/v0.1/countries/iso')
            .then(r => setCountries((r.data?.data ?? []).map(c => c.name)))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const c = filters.filterCountry;
        if (!c) { setCities([]); return; }
        setCitiesLoading(true);
        axios.post('https://countriesnow.space/api/v0.1/countries/cities', { country: c })
            .then(r => setCities(r.data?.data ?? []))
            .catch(() => setCities([]))
            .finally(() => setCitiesLoading(false));
    }, [filters.filterCountry]);

    const toggleDuration = d => setFilters(f => ({
        ...f,
        durations: f.durations.includes(d) ? f.durations.filter(x => x !== d) : [...f.durations, d],
    }));

    return (
        <>
            <div className={'gj-scrim' + (open ? ' is-open' : '')} onClick={onClose} />
            <div className={'gj-drawer' + (open ? ' is-open' : '')} role="dialog" aria-label="Filters">
                <div className="gj-drawer-head">
                    <span className="gj-drawer-title">Filters</span>
                    <button className="ix-btn ix-btn-ghost ix-btn-sm" onClick={onClose} style={{ padding: '6px' }}>
                        <X size={18} />
                    </button>
                </div>

                <div className="gj-drawer-body">
                    {/* Sort */}
                    <div className="gj-drawer-section">
                        <div className="gj-drawer-section-title">Sort by</div>
                        {SORT_OPTIONS.map(o => (
                            <button key={o.v}
                                className={'gj-radio-row' + (filters.sortBy === o.v ? ' is-active' : '')}
                                onClick={() => setFilters(f => ({ ...f, sortBy: o.v }))}>
                                <span className="gj-radio-dot" />{o.l}
                            </button>
                        ))}
                    </div>

                    {/* When */}
                    <div className="gj-drawer-section">
                        <div className="gj-drawer-section-title">When</div>
                        <div className="gj-chip-grid">
                            {WHEN_OPTIONS.map(o => (
                                <button key={o.v}
                                    className={'gj-chip-toggle' + (filters.when === o.v ? ' is-active' : '')}
                                    onClick={() => setFilters(f => ({ ...f, when: o.v }))}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Location */}
                    <div className="gj-drawer-section">
                        <div className="gj-drawer-section-title">Location</div>
                        <div className="gj-drawer-input" style={{ marginBottom: '8px' }}>
                            <MapPin size={15} color="var(--ink-400)" />
                            <select
                                value={filters.filterCountry}
                                onChange={e => {
                                    const c = e.target.value;
                                    setFilters(f => ({ ...f, filterCountry: c, filterCity: '', location: '' }));
                                }}
                                style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', color: filters.filterCountry ? 'var(--ink-900)' : 'var(--ink-400)' }}
                            >
                                <option value="">Any country</option>
                                {countries.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="gj-drawer-input">
                            <select
                                value={filters.filterCity}
                                onChange={e => {
                                    const city = e.target.value;
                                    setFilters(f => ({ ...f, filterCity: city, location: city }));
                                }}
                                disabled={!filters.filterCountry || citiesLoading}
                                style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', color: filters.filterCity ? 'var(--ink-900)' : 'var(--ink-400)' }}
                            >
                                <option value="">
                                    {!filters.filterCountry ? 'Select country first' : citiesLoading ? 'Loading cities…' : 'Any city'}
                                </option>
                                {cities.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Pay range */}
                    <div className="gj-drawer-section">
                        <div className="gj-drawer-section-title">Pay range</div>
                        <div className="gj-slider-label" style={{ marginBottom: '12px' }}>
                            <span>Min: <strong>€{filters.minPay}</strong></span>
                            <span>Max: <strong>{filters.maxPay >= 1500 ? '€1500+' : `€${filters.maxPay}`}</strong></span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '11px', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: '6px' }}>MINIMUM</label>
                                <input type="range" className="gj-slider" min={0} max={490} step={5}
                                    value={filters.minPay}
                                    onChange={e => setFilters(f => ({
                                        ...f, minPay: Math.min(Number(e.target.value), f.maxPay - 10)
                                    }))} />
                            </div>
                            <div>
                                <label style={{ fontSize: '11px', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)', display: 'block', marginBottom: '6px' }}>MAXIMUM</label>
                                <input type="range" className="gj-slider" min={10} max={1500} step={10}
                                    value={filters.maxPay}
                                    onChange={e => setFilters(f => ({
                                        ...f, maxPay: Math.max(Number(e.target.value), f.minPay + 10)
                                    }))} />
                            </div>
                        </div>
                    </div>

                    {/* Duration */}
                    <div className="gj-drawer-section">
                        <div className="gj-drawer-section-title">Duration</div>
                        <div className="gj-chip-grid">
                            {DURATION_OPTIONS.map(d => (
                                <button key={d}
                                    className={'gj-chip-toggle' + (filters.durations.includes(d) ? ' is-active' : '')}
                                    onClick={() => toggleDuration(d)}>
                                    {d}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="gj-drawer-section">
                        <div className="gj-drawer-section-title">Tags</div>
                        <FilterTagInput tags={filters.tags}
                            onChange={tags => setFilters(f => ({ ...f, tags }))} />
                    </div>

                    {/* Verified only */}
                    <div className="gj-drawer-section">
                        <div className="gj-drawer-section-title">Workers</div>
                        <button
                            className={'gj-chip-toggle' + (filters.verifiedOnly ? ' is-active' : '')}
                            onClick={() => setFilters(f => ({ ...f, verifiedOnly: !f.verifiedOnly }))}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Star size={13} /> Verified workers only
                        </button>
                        <p style={{ fontSize: '12px', color: 'var(--ink-500)', marginTop: '8px', marginBottom: 0 }}>
                            Shows jobs that require ID-verified workers.
                        </p>
                    </div>
                </div>

                <div className="gj-drawer-footer">
                    <button className="ix-btn ix-btn-outline" style={{ flex: 1 }} onClick={onReset}>Reset all</button>
                    <button className="ix-btn ix-btn-primary" style={{ flex: 2 }} onClick={onApply}>
                        Apply filters
                    </button>
                </div>
            </div>
        </>
    );
}

/* ─── Feed card ─── */
function FeedCard({ job, saved, onSave, onViewDetails }) {
    const tags    = jobTags(job);
    const reqs    = jobReqs(job);
    const name    = posterName(job);
    const ini     = name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const avatarBg = avatarColor(name);
    const accentColor = 'var(--teal-600, #14856a)';

    const handleShare = () => {
        const url = `${window.location.origin}/get-job/${job.id}`;
        if (navigator.share) navigator.share({ title: job.title, url }).catch(() => {});
        else navigator.clipboard?.writeText(url);
    };

    return (
        <article className="gj-feed-card">
            <div className="gj-card-box" style={{ borderTopColor: accentColor }}>
                {/* Content column */}
                <div className="gj-card-inner">
                    <div className="gj-card-top">
                        {tags[0] && <span className="gj-cat-chip">{tags[0]}</span>}
                        {(job.createdAt || job.dateTime) && (
                            <span className="gj-posted-time">Posted {timeAgo(job.createdAt || job.dateTime)}</span>
                        )}
                        {job.verifiedOnly && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                background: 'color-mix(in srgb,var(--teal-500) 12%,transparent)',
                                color: 'var(--teal-700)', padding: '3px 9px',
                                borderRadius: 'var(--r-pill)', fontSize: '10px',
                                fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
                            }}>
                                <Star size={10} /> Verified only
                            </span>
                        )}
                    </div>

                    <h2 className="gj-card-title">{job.title}</h2>

                    {(job.address || job.dateTime) && (
                        <div className="gj-where-when">
                            {job.address && (
                                <div className="gj-ww-cell">
                                    <div className="gj-ww-label"><MapPin size={11} /> Where</div>
                                    <div className="gj-ww-value">{job.address}</div>
                                </div>
                            )}
                            {job.dateTime && (
                                <div className="gj-ww-cell" style={{
                                    borderLeft: job.address ? '1px solid var(--ink-200)' : 'none',
                                }}>
                                    <div className="gj-ww-label"><Clock size={11} /> When</div>
                                    <div className="gj-ww-value">
                                        {formatDate(job.dateTime)}
                                        {formatTime(job.dateTime) && ` · ${formatTime(job.dateTime)}`}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="gj-card-pay">
                        <div className="gj-pay-big">€{job.payment}</div>
                        <div className="gj-pay-meta">
                            Fixed price{job.duration ? ` · ${job.duration}` : ''}
                        </div>
                    </div>

                    {job.description && <p className="gj-card-desc">{job.description}</p>}

                    {tags.length > 1 && (
                        <div className="gj-card-tags">
                            {tags.slice(1).map(t => <span key={t} className="gj-card-tag">{t}</span>)}
                        </div>
                    )}

                    {reqs.length > 0 && (
                        <div className="gj-card-reqs">
                            <div className="gj-card-reqs-title">Requirements</div>
                            <ul>{reqs.map((r, i) => <li key={i}>{r}</li>)}</ul>
                        </div>
                    )}

                    <div className="gj-card-foot">
                        <div className="gj-poster">
                            <div className="gj-poster-avatar" style={{ background: avatarBg, color: '#fff' }}>{ini}</div>
                            <div>
                                <div className="gj-poster-name">{name}</div>
                                <div className="gj-poster-meta">
                                    <Star size={11} color="var(--amber)" fill="var(--amber)" strokeWidth={0} />
                                    4.9 · {timeAgo(job.createdAt || job.dateTime)}
                                </div>
                            </div>
                        </div>
                        <button className="ix-btn ix-btn-outline ix-btn-sm" style={{ flexShrink: 0 }} onClick={() => onViewDetails(job.id)}>
                            View details
                        </button>
                    </div>
                </div>

                {/* Action rail */}
                <div className="gj-card-rail-inner">
                    <button className={'gj-action-btn' + (saved ? ' is-active' : '')} onClick={onSave} aria-label={saved ? 'Unsave' : 'Save'}>
                        <Heart size={20} fill={saved ? 'currentColor' : 'none'} />
                        <span className="gj-action-btn-label">{saved ? 'Saved' : 'Save'}</span>
                    </button>
                    <button className="gj-action-btn" onClick={handleShare} aria-label="Share">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                        </svg>
                        <span className="gj-action-btn-label">Share</span>
                    </button>
                    <button className="gj-apply-btn" aria-label="View details" onClick={() => onViewDetails(job.id)}>
                        <ArrowRight size={20} />
                        <span>Details</span>
                    </button>
                </div>
            </div>
        </article>
    );
}

/* ─── Feed view — dynamic height via ResizeObserver ─── */
function FeedView({ jobs, saved, toggleSave, onViewDetails, filterbarHeight }) {
    const containerRef = useRef(null);
    const [activeIdx, setActiveIdx] = useState(0);

    const cardH = `calc(100vh - 72px - ${filterbarHeight}px)`;

    const scrollTo = useCallback(i => {
        const el = containerRef.current;
        if (!el) return;
        el.children[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onScroll = () => {
            const h = el.clientHeight;
            if (h > 0) setActiveIdx(Math.round(el.scrollTop / h));
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        // Reset to top when jobs change (e.g. new filter applied)
        setActiveIdx(0);
        if (containerRef.current) containerRef.current.scrollTop = 0;
    }, [jobs]);

    useEffect(() => {
        const onKey = e => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); scrollTo(Math.min(activeIdx + 1, jobs.length - 1)); }
            if (e.key === 'ArrowUp'   || e.key === 'k') { e.preventDefault(); scrollTo(Math.max(activeIdx - 1, 0)); }
            if (e.key === 's') toggleSave(jobs[activeIdx]?.id);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [activeIdx, jobs, scrollTo, toggleSave]);

    if (jobs.length === 0) return (
        <div className="gj-empty" style={{ height: cardH }}>
            <Search size={40} style={{ opacity: 0.2 }} />
            <h3 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ink-700)', margin: 0 }}>No jobs match your filters.</h3>
            <p className="ix-body">Try clearing a filter or broadening your search.</p>
        </div>
    );

    return (
        <div className="gj-feed-stage" style={{ height: cardH }}>
            <div className="gj-feed-scroll" ref={containerRef}>
                {jobs.map(job => (
                    <FeedCard key={job.id} job={job}
                        saved={saved.has(job.id)}
                        onSave={() => toggleSave(job.id)}
                        onViewDetails={onViewDetails}
                    />
                ))}
            </div>
            <div className="gj-feed-rail">
                <button className="gj-nav-btn" onClick={() => scrollTo(activeIdx - 1)} disabled={activeIdx === 0} aria-label="Previous">
                    <ChevronUp size={16} />
                </button>
                <span className="gj-feed-counter mono">{activeIdx + 1} / {jobs.length}</span>
                <button className="gj-nav-btn" onClick={() => scrollTo(activeIdx + 1)} disabled={activeIdx >= jobs.length - 1} aria-label="Next">
                    <ChevronDown size={16} />
                </button>
            </div>
        </div>
    );
}

/* ─── List view ─── */
function ListView({ jobs, saved, toggleSave, onViewDetails }) {
    if (jobs.length === 0) return (
        <div className="gj-empty" style={{ padding: '80px 24px' }}>
            <Search size={40} style={{ opacity: 0.2 }} />
            <h3 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ink-700)', margin: 0 }}>No jobs found.</h3>
            <p className="ix-body">Try adjusting your filters.</p>
        </div>
    );

    return (
        <div className="gj-list">
            {jobs.map(job => {
                const tags = jobTags(job);
                return (
                    <article key={job.id} className="gj-list-row">
                        <div>
                            <div className="gj-list-tagline">
                                {tags.slice(0, 2).map(t => <span key={t} className="gj-chip-cat">{t}</span>)}
                                {job.duration && <span className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>{job.duration}</span>}
                                {job.verifiedOnly && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--teal-700)', fontFamily: 'var(--font-mono)' }}>
                                        <Star size={10} /> Verified only
                                    </span>
                                )}
                            </div>
                            <h3 className="gj-list-title">{job.title}</h3>
                            <div className="gj-list-meta">
                                {job.address && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><MapPin size={12} />{job.address}</span>}
                                {job.dateTime && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Clock size={12} />{formatDate(job.dateTime)}</span>}
                            </div>
                        </div>
                        <div>
                            <div className="gj-list-pay">
                                <div className="gj-list-pay-big">€{job.payment}</div>
                                <div className="gj-list-pay-meta mono">Fixed · {job.duration || 'TBD'}</div>
                            </div>
                            <div className="gj-list-actions">
                                <button className={'gj-save-btn' + (saved.has(job.id) ? ' is-saved' : '')}
                                    onClick={() => toggleSave(job.id)} aria-label="Save">
                                    <Heart size={15} fill={saved.has(job.id) ? 'currentColor' : 'none'} />
                                </button>
                                <button className="ix-btn ix-btn-primary ix-btn-sm" onClick={() => onViewDetails(job.id)}>View details</button>
                            </div>
                        </div>
                    </article>
                );
            })}
        </div>
    );
}

/* ─── Default filters ─── */
const DEFAULT_FILTERS = {
    search: '', sortBy: '', location: '',
    filterCountry: '', filterCity: '',
    minPay: 0, maxPay: 1500,
    durations: [], when: 'any', tags: [], verifiedOnly: false,
};

/* ─── Main component ─── */
const GetJob = ({ user }) => {
    const [jobs, setJobs]       = useState([]);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [view, setView]       = useState('feed');
    const [drawerOpen, setDrawerOpen]       = useState(false);
    const [saved, setSaved]                 = useState(new Set());
    const [loading, setLoading]             = useState(true);
    const [detailJobId, setDetailJobId]     = useState(null);
    // Measure filterbar height so FeedView cards fill the remaining viewport exactly
    const filterbarRef    = useRef(null);
    const [filterbarH, setFilterbarH] = useState(56);
    const locHook   = useLocation();

    /* ResizeObserver keeps filterbarH in sync as chips appear/disappear */
    useEffect(() => {
        const el = filterbarRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setFilterbarH(Math.ceil(entry.contentRect.height));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /* Load saved job IDs from backend on mount */
    useEffect(() => {
        const token = getToken();
        if (!token) return;
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/saved-jobs/ids`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(r => setSaved(new Set(r.data ?? []))).catch(() => {});
    }, []);

    /* Pick up ?search= and ?location= from URL */
    useEffect(() => {
        const p = new URLSearchParams(locHook.search);
        const q = p.get('search') || '';
        const loc = p.get('location') || '';
        setFilters(f => ({
            ...f,
            ...(q   ? { search: q } : {}),
            ...(loc ? { location: loc, filterCity: loc } : {}),
        }));
    }, []);

    /* Auto-apply user's country and city as location filter on first load */
    useEffect(() => {
        if (!user?.city && !user?.country) return;
        setFilters(f => {
            if (f.filterCity || f.filterCountry) return f;
            return {
                ...f,
                filterCountry: user.country || '',
                filterCity: user.city || '',
                location: user.city || '',
            };
        });
    }, [user?.city, user?.country]);

    /* Debounced backend fetch (300 ms) */
    useEffect(() => {
        const ctrl  = new AbortController();
        const timer = setTimeout(() => {
            const params = new URLSearchParams();
            if (filters.search)                   params.set('q',           filters.search);
            if (filters.location)                 params.set('location',    filters.location);
            if (filters.minPay > 0)               params.set('minPay',      String(filters.minPay));
            if (filters.maxPay < 1500)            params.set('maxPay',      String(filters.maxPay));
            // Send ALL selected durations as comma-separated — backend handles multi-match
            if (filters.durations.length > 0)     params.set('duration',    filters.durations.join(','));
            if (filters.tags.length)              params.set('tags',        filters.tags.join(','));
            if (filters.when && filters.when !== 'any') params.set('when', filters.when);
            if (filters.verifiedOnly)             params.set('verifiedOnly', 'true');
            if (filters.sortBy)                   params.set('sortBy',      filters.sortBy);

            setLoading(true);
            const endpoint = params.toString() ? `/api/jobs/search?${params}` : '/api/jobs';

            axios.get(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, { signal: ctrl.signal })
                .then(res => { setJobs(res.data); setLoading(false); })
                .catch(err => {
                    /* FIX: AbortController throws CanceledError, not an Axios cancel */
                    if (err.name !== 'CanceledError' && !axios.isCancel(err)) {
                        console.error(err);
                        setLoading(false);
                    }
                });
        }, 300);
        return () => { clearTimeout(timer); ctrl.abort(); };
    }, [filters]);

    const toggleSave = async id => {
        const token = getToken();
        const isSaved = saved.has(id);
        // Optimistic update
        setSaved(s => {
            const next = new Set(s);
            isSaved ? next.delete(id) : next.add(id);
            return next;
        });
        if (!token) return;
        try {
            if (isSaved) {
                await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/saved-jobs/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saved-jobs/${id}`, {}, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
        } catch {
            // Revert on error
            setSaved(s => {
                const next = new Set(s);
                isSaved ? next.add(id) : next.delete(id);
                return next;
            });
        }
    };

    /* Active filter chips */
    const activeChips = [];
    if (filters.search)        activeChips.push({ key: 'q',       label: `"${filters.search}"`,         clear: () => setFilters(f => ({ ...f, search: '' })) });
    if (filters.filterCity)    activeChips.push({ key: 'city',    label: `📍 ${filters.filterCity}`,    clear: () => setFilters(f => ({ ...f, filterCity: '', location: '' })) });
    if (filters.filterCountry && !filters.filterCity) activeChips.push({ key: 'country', label: `📍 ${filters.filterCountry}`, clear: () => setFilters(f => ({ ...f, filterCountry: '', filterCity: '', location: '' })) });
    if (filters.minPay > 0)   activeChips.push({ key: 'min', label: `€${filters.minPay}+`,     clear: () => setFilters(f => ({ ...f, minPay: 0 })) });
    if (filters.maxPay < 1500) activeChips.push({ key: 'max', label: `≤€${filters.maxPay}`,    clear: () => setFilters(f => ({ ...f, maxPay: 1500 })) });
    filters.durations.forEach(d => activeChips.push({ key: d, label: d, clear: () => setFilters(f => ({ ...f, durations: f.durations.filter(x => x !== d) })) }));
    if (filters.when && filters.when !== 'any') {
        const l = WHEN_OPTIONS.find(o => o.v === filters.when)?.l || filters.when;
        activeChips.push({ key: 'when', label: `🗓 ${l}`, clear: () => setFilters(f => ({ ...f, when: 'any' })) });
    }
    filters.tags.forEach(t => activeChips.push({ key: `tag-${t}`, label: `#${t}`, clear: () => setFilters(f => ({ ...f, tags: f.tags.filter(x => x !== t) })) }));
    if (filters.verifiedOnly) activeChips.push({ key: 'ver', label: '★ Verified only', clear: () => setFilters(f => ({ ...f, verifiedOnly: false })) });

    const filterCount = activeChips.length;

    return (
        <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>

            {/* Job detail modal overlay */}
            {detailJobId && (
                <JobDetailModal
                    jobId={detailJobId}
                    onClose={() => setDetailJobId(null)}
                    isSaved={saved.has(detailJobId)}
                    onToggleSave={() => toggleSave(detailJobId)}
                />
            )}

            {/* Filter bar — measured with ref */}
            <div className="gj-filterbar" ref={filterbarRef}>
                <div className="ix-container">
                    <div className="gj-filterbar-row">
                        <div className="gj-filterbar-search">
                            <Search size={16} color="var(--ink-400)" />
                            <input placeholder="Search jobs…" value={filters.search}
                                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
                            {filters.search && (
                                <button onClick={() => setFilters(f => ({ ...f, search: '' }))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-400)', display: 'flex' }}>
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <button className="ix-btn ix-btn-outline ix-btn-sm"
                            onClick={() => setDrawerOpen(true)}>
                            <SlidersHorizontal size={15} />
                            Filters
                            {filterCount > 0 && <span className="gj-filter-badge">{filterCount}</span>}
                        </button>

                        <div style={{ width: '1px', height: '24px', background: 'var(--ink-200)', flexShrink: 0 }} />

                        <div className="gj-view-switch">
                            <button className={'gj-view-btn' + (view === 'feed' ? ' is-active' : '')} onClick={() => setView('feed')}>Feed</button>
                            <button className={'gj-view-btn' + (view === 'list' ? ' is-active' : '')} onClick={() => setView('list')}>List</button>
                        </div>

                        <span className="mono" style={{ fontSize: '12px', color: 'var(--ink-500)', marginLeft: 'auto' }}>
                            {loading ? '…' : `${jobs.length} job${jobs.length !== 1 ? 's' : ''}`}
                        </span>
                    </div>

                    {activeChips.length > 0 && (
                        <div className="gj-chips-row">
                            {activeChips.map(chip => (
                                <button key={chip.key} className="gj-chip" onClick={chip.clear}>
                                    {chip.label} <X size={11} />
                                </button>
                            ))}
                            <button className="gj-chip-clear" onClick={() => setFilters(DEFAULT_FILTERS)}>
                                Clear all
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            {view === 'feed' ? (
                <FeedView jobs={jobs} saved={saved} toggleSave={toggleSave}
                    onViewDetails={setDetailJobId} filterbarHeight={filterbarH} />
            ) : (
                <ListView jobs={jobs} saved={saved} toggleSave={toggleSave}
                    onViewDetails={setDetailJobId} />
            )}

            {/* Filter drawer — writes directly to filters; Apply just closes */}
            <FilterDrawer open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                filters={filters}
                setFilters={setFilters}
                onApply={() => setDrawerOpen(false)}
                onReset={() => { setFilters(DEFAULT_FILTERS); setDrawerOpen(false); }}
            />
        </div>
    );
};

export default GetJob;
